import api from '@/api';
import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import store from './store';
import * as types from './store/mutation-types';

/* eslint max-len: 0 */
const ProductDetail = () => import(/* webpackChunkName: "product-detail" */ '@/views/product/ProductDetail');
const ProductList = () => import(/* webpackChunkName: "product-list" */ '@/views/product/ProductList');
const RakutenProductList = () => import(/* webpackChunkName: "rakuten-product-list" */ '@/views/rakuten/RakutenProductList');
const YahooProductList = () => import(/* webpackChunkName: "yahoo-product-list" */ '@/views/yahoo/YahooProductList');
const AmazonProductList = () => import(/* webpackChunkName: "amazone-product-list" */ '@/views/amazon/AmazonProductList');
const InformationDetail = () => import(/* webpackChunkName: "information-detail" */ '@/views/information/InformationDetail');

Vue.use(Router);
Vue.use(Meta);

const router = new Router({
  mode: 'history',
  linkExactActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
      };
    }

    if (from.name === to.name) {
      return false;
    }

    if (from.name === 'product' && to.name === 'product-has-specification') {
      return false;
    }
    return savedPosition || { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/plus/flink.php',
      redirect: '/',
    },
    {
      path: '/',
      name: 'home',
      component: () => import(/* webpackChunkName: "home" */ '@/views/Home.vue'),
    },
    {
      path: '/choose-country-region',
      name: 'choose-country-region',
      component: () => import(/* webpackChunkName: "choose-country-region" */ '@/views/ChooseRegion.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '@/views/auth/Login.vue'),
      meta: {
        needGuest: true,
      },
    },
    {
      path: '/register/:email?',
      name: 'register',
      component: () => import(/* webpackChunkName: "register" */ '@/views/auth/Register.vue'),
      meta: {
        needGuest: true,
      },
    },
    {
      path: '/reset',
      name: 'reset',
      component: () => import(/* webpackChunkName: "reset" */ '@/views/auth/Reset.vue'),
    },
    {
      path: '/reset-password/:token',
      name: 'ResetPassword',
      component: () => import(/* webpackChunkName: "reset-password" */ '@/views/auth/ResetPassword.vue'),
    },
    {
      path: '/activation-your-account',
      alias: '/thank-you-for-registered',
      name: 'activation-tips',
      component: () => import(/* webpackChunkName: "activation-tips" */ '@/views/auth/ActivationTips.vue'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/activation',
      name: 'activation',
      component: () => import(/* webpackChunkName: "activation" */ '@/views/auth/Activation.vue'),
    },
    {
      path: '/bind-platform',
      name: 'bind-platform',
      component: () => import(/* webpackChunkName: "activation" */ '@/views/auth/BindPlatform.vue'),
    },
    {
      path: '/:productId([0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12})',
      name: 'product',
      component: ProductDetail,
    },
    {
      path: '/:productId([0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12})-:specificationId(\\d+)',
      name: 'product-has-specification',
      component: ProductDetail,
    },
    {
      path: '/category/:id',
      name: 'category',
      component: ProductList,
    },
    {
      path: '/search',
      name: 'search',
      component: ProductList,
    },
    {
      path: '/source/:id',
      name: 'source',
      component: ProductList,
    },
    {
      path: '/brand/:id/:name',
      name: 'brand',
      component: ProductList,
    },
    {
      path: '/products/new-arrival',
      name: 'products:new',
      component: ProductList,
    },
    {
      path: '/products/recommend',
      name: 'products:recommend',
      component: ProductList,
    },
    {
      path: '/products/sale',
      name: 'products:sale',
      component: ProductList,
    },
    {
      path: '/products/hot-sales',
      name: 'products:hot-sales',
      component: ProductList,
    },
    {
      path: '/products',
      name: 'products',
      component: ProductList,
    },
    {
      path: '/product/tag/:id/:name?',
      name: 'tag',
      component: ProductList,
    },
    {
      path: '/home',
      component: () => import(/* webpackChunkName: "user-home" */ '@/views/user'),
      children: [
        {
          path: '/',
          name: 'dashboard',
          component: () => import(/* webpackChunkName: "dashboard" */ '@/views/user/Dashboard'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import(/* webpackChunkName: "orders" */ '@/views/user/Orders'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'shipping-address',
          name: 'shipping-address',
          component: () => import(/* webpackChunkName: "shipping-address" */ '@/views/user/ShippingAddress'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'wishlist',
          name: 'wishlist',
          component: () => import(/* webpackChunkName: "wishlist" */ '@/views/user/WishList'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'history',
          name: 'history',
          component: () => import(/* webpackChunkName: "history" */ '@/views/user/History'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'information',
          name: 'information',
          component: () => import(/* webpackChunkName: "userinfo" */ '@/views/user/UserInfo'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'binding',
          name: 'binding',
          component: () => import(/* webpackChunkName: "binding" */ '@/views/user/Binding'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'proxy-request',
          name: 'proxy-request-list',
          component: () => import(/* webpackChunkName: "proxy-request-list" */ '@/views/user/ProxyList'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'commission',
          name: 'commission',
          component: () => import(/* webpackChunkName: "proxy-request-list" */ '@/views/user/Commission'),
          meta: {
            needAuthenticate: true,
          },
        },
        {
          path: 'coupons',
          name: 'coupons',
          component: () => import(/* webpackChunkName: "proxy-request-list" */ '@/views/user/Coupon'),
          meta: {
            needAuthenticate: true,
          },
        },
      ],
    },
    {
      path: '/order/:id',
      name: 'order-detail',
      component: () => import(/* webpackChunkName: "order-detail" */ '@/views/order/OrderDetail'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/order-payment/:id',
      name: 'order-payment',
      component: () => import(/* webpackChunkName: "order-payment" */ '@/views/order/OrderPayment'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/order-pay/:id',
      name: 'order-pay',
      component: () => import(/* webpackChunkName: "order-pay" */ '@/views/order/OrderPay'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/faq',
      component: () => import(/* webpackChunkName: "customer-service" */ '@/views/customerService'),
      children: [
        {
          path: '/',
          name: 'faq',
          component: () => import(/* webpackChunkName: "faq" */ '@/views/customerService/FAQ'),
        },
        {
          path: '/contacts',
          name: 'contacts',
          component: () => import(/* webpackChunkName: "contacts" */ '@/views/customerService/Contacts'),
        },
        {
          path: '/guide',
          name: 'guide',
          component: () => import(/* webpackChunkName: "guide" */ '@/views/customerService/Guide'),
        },
      ],
    },
    {
      path: '/news',
      component: () => import(/* webpackChunkName: "information" */ '@/views/information'),
      children: [
        {
          path: '/',
          name: 'news',
          component: InformationDetail,
        },
        {
          path: '/terms-and-conditions',
          name: 'terms-and-conditions',
          component: InformationDetail,
        },
        {
          path: '/delivery_schedule',
          name: 'delivery_schedule',
          component: InformationDetail,
        },
        {
          path: '/privacy-policy',
          name: 'privacy-policy',
          component: InformationDetail,
        },
        {
          path: '/about-us',
          name: 'about-us',
          component: InformationDetail,
        },
      ],
    },
    {
      path: '/proxy-request/:id?',
      name: 'proxy-request',
      component: () => import(/* webpackChunkName: "proxy-request" */ '@/views/proxy/ProxyRequest'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import(/* webpackChunkName: "cart" */ '@/views/checkout/Cart'),
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import(/* webpackChunkName: "checkout" */ '@/views/checkout/Checkout'),
      meta: {
        needAuthenticate: true,
      },
    },
    {
      path: '/rakuten',
      name: 'rakuten',
      component: RakutenProductList,
    },
    {
      path: '/rakuten/category/:id',
      name: 'rakuten-category',
      component: RakutenProductList,
    },
    {
      path: '/rakuten/search',
      name: 'rakuten-search',
      component: RakutenProductList,
    },
    {
      path: '/rakuten/item/:itemCode',
      name: 'rakuten-product-detail',
      component: () => import(/* webpackChunkName: "rakuten-product-detail" */ '@/views/rakuten/RakutenProductDetail'),
    },
    {
      path: '/yahoo/jp',
      name: 'yahoo_jp',
      component: YahooProductList,
    },
    {
      path: '/yahoo/jp/category/:id',
      name: 'yahoo_jp-category',
      component: YahooProductList,
    },
    {
      path: '/yahoo/jp/search',
      name: 'yahoo_jp-search',
      component: YahooProductList,
    },
    {
      path: '/yahoo/jp/item/:code',
      name: 'yahoo_jp-product-detail',
      component: () => import(/* webpackChunkName: "yahoo-product-detail" */ '@/views/yahoo/YahooProductDetail'),
    },
    {
      path: '/amazon',
      name: 'amazon',
      component: AmazonProductList,
    },
    {
      path: '/amazon/category/:id',
      name: 'amazon-category',
      component: AmazonProductList,
    },
    {
      path: '/amazon/item/:code',
      name: 'amazon-product-detail',
      component: import(/* webpackChunkName: "amazon-product-detail" */ '@/views/amazon/AmazonProductDetail'),
    }, {
      path: '/shop',
      name: 'shop-index',
      component: () => import(/* webpackChunkName: "shop-index" */ '@/views/directPurchase/DirectPurchaseList.vue'),
    },
    {
      path: '/brand-list',
      name: 'brand-list',
      component: () => import(/* webpackChunkName: "brand-list" */ '@/views/brands/brands'),
    },
    {
      path: '/comment/:id',
      name: 'comment',
      component: () => import(/* webpackChunkName: "product-comment" */ '@/views/product/Comment'),
    },
    // {
    //   path: '/transportation',
    //   component: Transportation,
    //   children: [
    //     {
    //       path: '/',
    //       name: 'parcel',
    //       component: Parcel,
    //     },
    //     {
    //       path: 'warehouse',
    //       name: 'warehouse',
    //       component: Warehouse,
    //     },
    //     {
    //       path: 'create-order',
    //       name: 'transportation:create-order',
    //       component: CreateOrder,
    //     },
    //   ],
    // },
    {
      path: '/coupon-link/:code',
      name: 'coupon',
      component: () => import(/* webpackChunkName: "cart" */ '@/views/coupons/Coupons.vue'),
    },
    {
      path: '/admin-login',
      name: 'admin-login',
      component: () => import(/* webpackChunkName: "admin-login" */ '@/views/auth/AdminLogin.vue'),
    },
    {
      path: '*',
      component: () => import(/* webpackChunkName: "404" */ '@/views/NotFound'),
    },
  ],
});

router.beforeEach((to, from, next) => {
  const { isLogined, currentUser } = store.getters;
  if (to.name === 'order-detail' && to.query.token) {
    // console.log(base_64decrypt(query.token));
    // const token = `Bearer ${to.query.token}`;
    // console.log(token);7200
    // return;
    store.commit(types.RECEIVE_USER_TOKEN, { token: to.query.token, expires_in: 7200 });
    // return dispatch('getIdentity');
    // store.commit(types.SET_TOKEN, to.query.token);
    store.dispatch('getIdentity');
    // console.log(to);
    next();
  }
  if (to.path !== '/activation' && to.path !== '/activation-your-account' && isLogined && currentUser && currentUser.status === 2) {
    next({ path: '/activation-your-account' });
    return;
  }
  // 用戶已登錄，路由需要訪客身份
  if (to.meta.needGuest && isLogined) {
    next({ path: '/' });
    return;
  }
  // 用戶未登錄，路由需要用戶身份
  if (to.meta.needAuthenticate && !isLogined) {
    store.dispatch('setFromRoute', to);
    next({ name: 'login' });
    return;
  }
  next();
});

router.afterEach((to, from) => {
  const params = {
    to_path: to.fullPath,
    from_path: from.fullPath,
    query: to.query,
    params: to.params,
  };
  api.user.viewLog(params);

  /* global _hmt */
  // 百度统计
  if (process.env.NODE_ENV !== 'development') {
    _hmt.push(['_trackPageview', to.path]);
  }
});

export default router;
